import React from 'react';
import { ButtonProps } from '../../types';

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  icon,
  iconPosition = 'none',
  loading = false,
  disabled = false,
  children,
  onClick,
  type = 'button',
  className = '',
}) => {
  const baseClasses =
    'inline-flex items-center justify-center font-normal rounded-pill transition-[color,background-color,border-color,box-shadow,transform,opacity] duration-normal ease-apple focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-900/20 focus-visible:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed';

  const variantClasses = {
    primary:
      'bg-neutral-900 text-white hover:bg-neutral-800 active:scale-[0.98]',
    secondary:
      'bg-white text-neutral-900 border border-neutral-200/90 hover:bg-neutral-50 hover:border-neutral-300',
    ghost: 'text-neutral-900 hover:bg-neutral-50/80',
    link: 'text-primary-500 hover:text-neutral-900 rounded-none px-0',
    success: 'bg-success-500 text-white hover:bg-success-600',
    error: 'bg-error-500 text-white hover:bg-error-600',
    warning: 'bg-warning-500 text-white hover:bg-warning-600',
    info: 'bg-neutral-900 text-white hover:bg-neutral-800',
  };

  const sizeClasses = {
    sm: 'px-5 py-2 text-[13px]',
    md: 'px-6 py-2.5 text-[15px]',
    lg: 'px-8 py-3 text-[17px]',
  };

  const iconSizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-4 h-4',
    lg: 'w-[18px] h-[18px]',
  };

  const iconSpacingClasses = {
    sm: 'gap-1.5',
    md: 'gap-2',
    lg: 'gap-2',
  };

  const classes = `
    ${baseClasses}
    ${variantClasses[variant]}
    ${sizeClasses[size]}
    ${icon !== undefined ? iconSpacingClasses[size] : ''}
    ${className}
  `.trim();

  const iconElement = icon && (
    <span className={`${iconSizeClasses[size]} flex-shrink-0`}>{icon}</span>
  );

  const loadingSpinner = (
    <svg className={`${iconSizeClasses[size]} animate-spin`} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );

  const renderContent = () => {
    if (loading) {
      return (
        <>
          {loadingSpinner}
          <span>Chargement...</span>
        </>
      );
    }

    if (icon && iconPosition === 'left') {
      return (
        <>
          {iconElement}
          {children}
        </>
      );
    }

    if (icon && iconPosition === 'right') {
      return (
        <>
          {children}
          {iconElement}
        </>
      );
    }

    return children;
  };

  return (
    <button
      type={type}
      className={classes}
      onClick={onClick}
      disabled={disabled || loading}
      aria-disabled={disabled || loading}
    >
      {renderContent()}
    </button>
  );
};

export default Button;
